<template>
  <div class="grid gap-2 bg-gray-50 border-2 rounded-xl border-gray-200 p-2">
    <div class="flex flex-wrap gap-2 items-center justify-between">
      <div class="grow flex gap-2 items-center">
        <CircleCheck
          v-if="override.requires_review_from !== 'agent'"
          class="h-4.5 w-4.5 shrink-0 text-gray-600"
        />
        <CircleCheck
          v-else-if="['approved', 'retired'].includes(override.status)"
          class="h-4.5 w-4.5 shrink-0 text-green-600"
        />
        <CircleX
          v-else-if="override.status === 'denied'"
          class="h-4.5 w-4.5 shrink-0 text-red-600"
        />
        <CirclePause
          v-else-if="override.status === 'pending'"
          class="h-4.5 w-4.5 shrink-0 text-yellow-600"
        />
        <div class="flex flex-wrap items-baseline">
          <span class="font-medium text-nowrap mr-1.5">{{
            formatDate(override.last_reviewed_or_created)
          }}</span>

          <span class="text-sm @2xl:ml-0 @2xl:block text-nowrap">{{
            formatTime(override.last_reviewed_or_created)
          }}</span>
        </div>
      </div>

      <div class="flex gap-2 text-sm">
        <div
          class="w-fit px-2 py-1 rounded font-semibold"
          :class="{
            'bg-gray-200 text-gray-600':
              override.requires_review_from !== 'agent',
            'bg-green-200 text-green-900':
              (override.status === 'approved' ||
                override.status === 'retired') &&
              override.requires_review_from === 'agent',
            'bg-red-200 text-red-900': override.status === 'denied',
            'bg-yellow-200 text-yellow-950': override.status === 'pending',
          }"
        >
          {{ getStatus(override) }}
        </div>
        <div
          v-if="override.modified_by_reviewer"
          class="w-fit px-2 py-1 rounded font-semibold bg-gray-200 text-gray-600"
        >
          edited by <span>Agent/ATM</span>
        </div>
        <div
          v-if="override.status === 'retired'"
          class="w-fit px-2 py-1 rounded font-semibold bg-gray-200 text-gray-600"
        >
          retired
        </div>
      </div>
    </div>

    <div class="px-2 py-1 bg-white rounded-lg transition-height">
      <button
        class="bg-white rounded flex items-center gap-2 text-sm text-gray-900 font-normal p-0 w-full"
        @click="showAdditionalDetails = !showAdditionalDetails"
      >
        Additional details
        <ChevronDown
          v-if="true"
          class="w-3 h-3 transition-transform duration-200"
          :class="{ 'rotate-x-180': showAdditionalDetails }"
        />
      </button>

      <div v-if="showAdditionalDetails" class="text-sm mt-1">
        <div>
          <span class="font-semibold">Date created:</span>
          {{ formatDate(override.date_created) }},
          {{ formatTime(override.date_created) }}
        </div>
        <div v-if="override.date_reviewed">
          <span class="font-semibold">Date reviewed:</span>
          {{ formatDate(override.date_reviewed) }},
          {{ formatTime(override.date_reviewed) }}
        </div>
        <div v-if="override.status_detail">
          <span class="font-semibold">Denial reason:</span>
          {{ override.status_detail }}
        </div>
        <div v-if="override.additional_feedback">
          <span class="font-semibold">
            Additional feedback from the agent/ATM:</span
          >
          {{ override.additional_feedback }}
        </div>
        <button
          v-if="showMirusControls && override.requires_review_from === 'agent'"
          class="bg-orange-100 hover:bg-orange-200 mt-2 text-orange-900 px-1.5 py-1 text-xs text-nowrap border-2 border-dashed border-orange-500"
          @click="showFeedbackModal = true"
        >
          <span v-if="override.additional_feedback">edit feedback</span
          ><span v-else>add feedback</span>
        </button>
      </div>
    </div>
    <RichTextDisplay
      class="bg-white max-w-full overflow-x-auto"
      :content="override.value"
    />
  </div>

  <Modal v-show="showFeedbackModal" @close="showDeleteConfirm = false">
    <template v-slot:header>
      <h1 class="text-xl font-bold uppercase">
        <span v-if="showDeleteConfirm">Are you sure?</span>
        <span v-else>
          <span v-if="override?.additional_feedback">Edit feedback</span
          ><span v-else>Add feedback</span>
        </span>
      </h1>
    </template>
    <template v-slot:body>
      <div v-if="showDeleteConfirm">
        <p>This feedback cannot be recovered once deleted.</p>
      </div>
      <div v-else>
        <textarea v-model="mirusFeedback" class="w-full min-h-40"></textarea>
      </div>
    </template>
    <template v-slot:footer>
      <div v-if="showDeleteConfirm" class="flex gap-2 justify-between">
        <button
          @click="showDeleteConfirm = false"
          class="bg-gray-100 hover:bg-gray-200 text-gray-900"
        >
          Cancel
        </button>
        <button
          class="w-20 bg-red-100 hover:bg-red-200 text-red-900"
          @click="deleteFeedback"
        >
          <span v-if="submittingFeedback"> <LoadingSpinner small /> </span
          ><span v-else>Delete</span>
        </button>
      </div>
      <div v-else class="flex gap-2 justify-between">
        <div>
          <button
            v-if="override?.additional_feedback"
            @click="showDeleteConfirm = true"
            class="bg-red-100 hover:bg-red-200 text-red-900"
          >
            Delete
          </button>
        </div>
        <div class="flex gap-2">
          <button
            @click="showFeedbackModal = false"
            class="bg-gray-100 hover:bg-gray-200 text-gray-900"
          >
            Cancel
          </button>
          <button class="w-20" @click="submitFeedback">
            <span v-if="submittingFeedback">
              <LoadingSpinner small gray /> </span
            ><span v-else>Submit</span>
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { useCookies } from "@vueuse/integrations/useCookies";
import { formatRelative, parseISO } from "date-fns";
import { ref } from "vue";
import RichTextDisplay from "@/components/editor/RichTextDisplay.vue";
import ChevronDown from "@/components/icons/ChevronDown.vue";
import CircleCheck from "@/components/icons/CircleCheck.vue";
import CircleExclamation from "@/components/icons/CircleExclamation.vue";
import CirclePause from "@/components/icons/CirclePause.vue";
import CircleX from "@/components/icons/CircleX.vue";
import Clock from "@/components/icons/Clock.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Modal from "@/components/Modal.vue";
import useDomainStore from "@/stores/domain";
import useNotificationStore from "@/stores/notifications";

const domainStore = useDomainStore();
const notificationStore = useNotificationStore();
const cookies = useCookies(["csrftoken"]);

const props = defineProps({
  override: {
    type: Object,
    required: true,
  },
  showMirusControls: {
    type: Boolean,
    default: false,
  },
});

const showFeedbackModal = ref(false);
const showDeleteConfirm = ref(false);
const showAdditionalDetails = ref(false);
const submittingFeedback = ref(false);
const mirusFeedback = ref(props.override.additional_feedback);

async function deleteFeedback() {
  mirusFeedback.value = "";
  submitFeedback();
}

async function submitFeedback() {
  submittingFeedback.value = true;
  const response = await fetch(
    `/api/domains/${domainStore.domain.name}/overrides/${props.override.pk}/?status=${props.override.status}`,
    {
      method: "PATCH",
      body: JSON.stringify({ additional_feedback: mirusFeedback.value }),
      headers: {
        "X-CSRFToken": cookies.get("csrftoken"),
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    notificationStore.addNotification("Feedback submitted!", "green", 2000);
  } else {
    const errorText = await response.text();
    notificationStore.addNotification(
      `Failed to submit feedback, ${errorText}. Please try again later.`,
      "red",
      2000,
    );
  }
  await domainStore.getOverrides(
    domainStore.domain.name,
    props.override.override_type,
    props.override.subkey ?? null,
  );
  submittingFeedback.value = false;
}

function getStatus(override) {
  if (override.requires_review_from !== "agent") {
    return "created by agent";
  }
  if (override.status === "retired") {
    return "approved";
  }
  return override.status;
}

function formatDate(isoDate) {
  return parseISO(isoDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatTime(isoDate) {
  return parseISO(isoDate).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
</script>
